import { useState, useEffect } from 'react';
import { useQuery } from './QueryContext';
import styles from './contents.module.css';
import Header from './components/Header';
import SideBar from './components/SideBar';
import QueryPlatform from './components/QueryPlatform';
import QueryContainer from './components/QueryContainer';
import ListTitle from './components/ListTitle';
import ListHeader from './components/ListHeader';
import { downloadJson, sanitizeFilenamePart } from './utils/downloadJson';
import { downloadBlob } from './utils/downloadFile';
import { toCsv, formatCommentsForCsv } from './utils/toCsv';
import CommentsModal from './components/CommentsModal';

const proxyImg = (url) => {
  if (!url) return '';
  if (/scontent[^/]*\.cdninstagram\.com|\.fbcdn\.net/.test(url)) {
    return `/api/proxy?url=${encodeURIComponent(url)}`;
  }
  return url;
};

const getCountryFlag = (countryCode) => {
  if (!countryCode) return '';
  return `https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`;
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  } catch (error) {
    console.error('Date formatting error:', error);
    return dateString;
  }
};

const formatNumber = (num) => {
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return num;
};

const mapContentsItem = (item, platform, selectedCountry) => {
  const isTiktok = platform === 'tiktok';
  const isYoutube = platform === 'youtube';
  return {
    rank: item.rank,
    description: isTiktok
      ? (item.description || '')
      : isYoutube
        ? (item.title || '')
        : (item.description || ''),
    views: isTiktok ? (item.play_count || 0) : isYoutube ? (item.views || 0) : (item.video_play_count || 0),
    engagement: isTiktok ? (item.digg_count || 0) : isYoutube ? (item.likes || 0) : (item.likes || 0),
    comments: isTiktok ? (item.comment_count || 0) : (item.num_comments || 0),
    date: isTiktok ? (item.create_time || '') : (item.date_posted || ''),
    country: isTiktok ? (item.country || '') : isYoutube ? selectedCountry : (item.country || ''),
    url: item.url || '',
    creator: isTiktok ? (item.profile_avatar || '') : isYoutube ? (item.avatar_img_channel || '') : (item.profile_image_link || ''),
    creator_url: isTiktok ? (item.profile_url || '') : isYoutube ? (item.channel_url || '') : `https://www.instagram.com/${item.user_posted || ''}`,
    thumbnail: isTiktok ? (item.preview_image || '') : isYoutube ? (item.preview_image || '') : (item.thumbnail || ''),
    comments_list: (item.comments || []).map(c => ({
      commenter_name: c.commenter_name || '',
      comment_text: c.comment_text || '',
      num_likes: c.num_likes || 0,
      num_replies: c.num_replies || 0,
      comment_date: c.comment_date || '',
    })),
  };
};

const ListItem = ({ rank, country, description, views, engagement, comments, date, thumbnail, url, creator, creatorlink, onCommentsClick, hasComments }) => {
  const handleThumbnailClick = () => {
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleCreatorClick = () => {
    if (creatorlink) window.open(creatorlink, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className={styles.topListItem}>
      <div className={styles.frameParent}>
        <div className={styles.rankWrapper}>
          <div className={styles.rank}>{rank}</div>
        </div>
        <div
          className={styles.contentsWrapper}
          onClick={handleThumbnailClick}
          style={{ cursor: url ? 'pointer' : 'default' }}
        >
          <img className={styles.contents} src={thumbnail} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        </div>
        <div className={styles.descriptionWrapper}>
          <div className={styles.description}>{description}</div>
        </div>
        <div
          className={styles.creatorWrapper}
          onClick={handleCreatorClick}
          style={{ cursor: creatorlink ? 'pointer' : 'default' }}
        >
          <img className={styles.creator} alt="" src={creator} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'; }} />
        </div>
        <div className={styles.countryWrapper}>
          <img className={styles.countryFlag} src={getCountryFlag(country)} alt={`${country} flag`} />
        </div>
        <div className={styles.viewsWrapper}>
          <div className={styles.counterWrapper}>
            <div className={styles.listText}>{formatNumber(views)}</div>
          </div>
        </div>
        <div className={styles.engagementWrapper}>
          <div className={styles.counterWrapper}>
            <div className={styles.listText}>{formatNumber(engagement)}</div>
          </div>
        </div>
        <div
          className={styles.commentsWrapper}
          onClick={hasComments ? onCommentsClick : undefined}
          style={{ cursor: hasComments ? 'pointer' : 'default' }}
        >
          <div className={styles.counterWrapper}>
            <div
              className={styles.listText}
              style={{ color: hasComments ? '#3b82f6' : '#000' }}
            >
              {formatNumber(comments)}
            </div>
          </div>
        </div>
        <div className={styles.postedDateWrapper}>
          <div className={styles.listText}>{formatDate(date)}</div>
        </div>
      </div>
      <img className={styles.horizontalfullWidthIcon} alt="" />
    </div>
  );
};

const Contents = () => {
  const [contentsList, setContentsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [commentsModal, setCommentsModal] = useState(null); // { comments }
  const [dateRange, setDateRange] = useState({ minDate: null, maxDate: null });
  const { selectedPlatform, setSelectedPlatform, selectedCountry, selectedCategory, selectedSubCategory, selectedDate, setSelectedDate } = useQuery();

  useEffect(() => {
    if (!selectedPlatform || !selectedCountry || !selectedCategory) return;
    let cancelled = false;
    fetch(`/api/dates?platform=${encodeURIComponent(selectedPlatform)}&country=${encodeURIComponent(selectedCountry)}&category=${encodeURIComponent(selectedCategory)}&subcategory=${encodeURIComponent(selectedSubCategory)}`)
      .then(r => r.json())
      .then(d => {
        if (cancelled || !d.success) return;
        setDateRange({
          minDate: d.data.minDate ? new Date(d.data.minDate) : null,
          maxDate: d.data.maxDate ? new Date(d.data.maxDate) : null,
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedPlatform, selectedCountry, selectedCategory, selectedSubCategory]);

  useEffect(() => {
    let cancelled = false;
    const fetchContentsList = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!selectedPlatform || !selectedCountry || !selectedCategory) {
          if (cancelled) return;
          setContentsList([]);
          setLoading(false);
          return;
        }

        const dateParam = selectedDate
          ? `&date=${selectedDate.toISOString().slice(0, 10)}`
          : '';
        const response = await fetch(
          `/api/contents?platform=${encodeURIComponent(selectedPlatform)}&category=${encodeURIComponent(selectedCategory)}&subcategory=${encodeURIComponent(selectedSubCategory)}&country=${encodeURIComponent(selectedCountry)}${dateParam}`
        );

        if (cancelled) return;
        if (!response.ok) throw new Error('Failed to fetch contents list');

        const data = await response.json();
        if (cancelled) return;
        if (data.success) {
          const rankedItems = (data.data.items || []).sort((a, b) => {
            return (b.views_daily || 0) - (a.views_daily || 0);
          }).map((item, index) => ({ ...item, rank: index + 1 }));
          setContentsList(rankedItems);
          if (data.data.date) {
            const apiDate = new Date(data.data.date).toISOString().slice(0, 10);
            const curDate = selectedDate ? selectedDate.toISOString().slice(0, 10) : null;
            if (apiDate !== curDate) setSelectedDate(new Date(data.data.date));
          }
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching contents list:', err);
        setError(err.message);
        setContentsList([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchContentsList();
    return () => { cancelled = true; };
  }, [selectedPlatform, selectedCountry, selectedCategory, selectedSubCategory, selectedDate]);

  const handleDownload = (format) => {
    const items = contentsList.map((item) =>
      mapContentsItem(item, selectedPlatform, selectedCountry)
    );
    const dateStr = selectedDate
      ? selectedDate.toISOString().slice(0, 10)
      : 'unknown';
    const payload = {
      page: 'contents',
      platform: selectedPlatform,
      category: selectedCategory,
      country: selectedCountry,
      date: dateStr,
      exported_at: new Date().toISOString(),
      count: items.length,
      items,
    };
    const baseName = [
      'contents',
      sanitizeFilenamePart(selectedPlatform),
      sanitizeFilenamePart(selectedCategory),
      sanitizeFilenamePart(selectedCountry),
      sanitizeFilenamePart(dateStr),
    ].join('-');
    if (format === 'csv') {
      const csvItems = payload.items.map(({ comments_list, ...rest }) => ({
        ...rest,
        comments_text: formatCommentsForCsv(comments_list),
      }));
      downloadBlob(toCsv(csvItems), baseName + '.csv', 'text/csv;charset=utf-8');
    } else {
      downloadJson(payload, baseName + '.json');
    }
  };

  return (
    <div className={styles.contentsListTyp}>
      <Header />
      <div className={styles.mainContent}>
        <SideBar />
        <div className={styles.body}>
          <QueryPlatform onPlatformChange={setSelectedPlatform} />
          <QueryContainer />
          <ListTitle
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            minDate={dateRange.minDate}
            maxDate={dateRange.maxDate}
            onDownload={handleDownload}
            disabled={loading || contentsList.length === 0}
          />
          <ListHeader />
          <img className={styles.horizontalfullWidthIcon2} alt="" />
          {loading ? (
            <div className={styles.loading}>Loading...</div>
          ) : error ? (
            <div className={styles.noData}>No data available.</div>
          ) : contentsList.length === 0 ? (
            <div className={styles.noData}>No data available.</div>
          ) : (
            contentsList.map((item, index) => {
              const platform = selectedPlatform;
              const isTiktok = platform === 'tiktok';
              const isYoutube = platform === 'youtube';
              return (
              <ListItem
                key={index}
                rank={item.rank}
                thumbnail={isTiktok ? (item.preview_image || '') : isYoutube ? (item.preview_image || '') : proxyImg(item.thumbnail)}
                url={item.url || ''}
                description={isTiktok ? (item.description || 'No description') : isYoutube ? (item.title || 'No description') : (item.description || 'No description')}
                creator={isTiktok ? (item.profile_avatar || '') : isYoutube ? (item.avatar_img_channel || '') : proxyImg(item.profile_image_link)}
                creatorlink={isTiktok ? (item.profile_url || '') : isYoutube ? (item.channel_url || '') : `https://www.instagram.com/${item.user_posted || ''}`}
                country={isTiktok ? (item.country || '') : isYoutube ? selectedCountry : (item.country || '')}
                views={isTiktok ? (item.play_count || 0) : isYoutube ? (item.views || 0) : (item.video_play_count || 0)}
                engagement={isTiktok ? (item.digg_count || 0) : isYoutube ? (item.likes || 0) : (item.likes || 0)}
                comments={isTiktok ? (item.comment_count || 0) : isYoutube ? (item.num_comments || 0) : (item.num_comments || 0)}
                date={isTiktok ? (item.create_time || '') : isYoutube ? (item.date_posted || '') : (item.date_posted || '')}
                hasComments={!!item.has_scraped_comments}
                onCommentsClick={() => setCommentsModal({ comments: item.comments || [] })}
              />
              );
            })
          )}
        </div>
      </div>
      {commentsModal && (
        <CommentsModal
          comments={commentsModal.comments}
          onClose={() => setCommentsModal(null)}
        />
      )}
    </div>
  );
};

export default Contents;
